import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DialogConfirmSendLegacyWebhooksComponent } from '@app/modules/apps/components/dialog-confirm-send-legacy-webhooks/dialog-confirm-send-legacy-webhooks.component';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { NotificationType } from '@app/shared/interfaces';
import { Webhook, WebhookEventType, WebhookResponse, WebhookType } from '@app/shared/interfaces/lib/webhook.interface';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { WebhooksService } from '@app/shared/services/webhooks/webhooks.service';
import { map } from 'rxjs/operators';

interface WebhookEntry {
  data: Webhook;
  type: WebhookType;
  editingUrl: boolean;
  saveStatus: number;
  testing: boolean;
  testResult: null | boolean;
  sendStatus: null | number;
  pendingTypes?: WebhookEventType[];
}

@Component({
  standalone: false,
  selector: 'sbnb-webhooks-next',
  templateUrl: './webhooks-next.component.html',
  styleUrls: ['./webhooks-next.component.scss'],
})
export class WebhooksNextComponent implements OnInit {
  learnMoreNextUrl = `https://my.hospitable.com/support-documentation/article/10008203-v2-webhooks-for-hosts`;
  loading: boolean;
  adding: boolean;
  saveStatus = 0;
  activeWebhooks: WebhookEntry[] = [];
  newWebhook: Webhook;
  webhookEventTypes = WebhookEventType;
  WebhookEventType = WebhookEventType;

  // Preserve original order of enum
  originalOrder = () => 0;

  constructor(
    private titleService: TitleService,
    private toast: ToastNotificationsService,
    private webhookService: WebhooksService,
    private dialog: MatDialog
  ) {
    this.newWebhook = { id: null, name: null, url: null, types: [] };
  }

  toSentenceCase(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  ngOnInit() {
    this.titleService.manuallySet('Webhooks');
    this.loadWebhooks();
  }

  loadWebhooks() {
    this.loading = true;
    this.webhookService
      .getWebhooks()
      .pipe(
        map((res: Webhook[]) => {
          return res.map((webhook: Webhook) => ({
            data: webhook,
            type: WebhookType.Next,
          })) as WebhookEntry[];
        })
      )
      .subscribe((webhookEntries: WebhookEntry[]) => {
        this.loading = false;
        this.activeWebhooks = webhookEntries;
      });
  }

  validUrl(url: string) {
    return url.startsWith('http://') || url.startsWith('https://');
  }

  addWebhook() {
    if (!this.newWebhook.url) {
      return;
    }

    if (this.validUrl(this.newWebhook.url)) {
      this.saveStatus = 1;

      this.webhookService.addWebhook(this.newWebhook).subscribe((response) => {
        const webhook = { data: response.webhook, type: WebhookType.Next } as WebhookEntry;
        this.handleSavedStatus(webhook, response);
        this.activeWebhooks.unshift(webhook);
        this.newWebhook = { id: null, name: null, url: null, types: [] };
        this.adding = false;
      });
    } else {
      this.toast.open(`Webhooks should start with 'http://' or 'https://'`, 'Dismiss', NotificationType.Error);
    }
  }

  deleteWebhookPrompt(webhook: WebhookEntry) {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        name: 'webhook',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteWebhook(webhook);
      }
    });
  }

  deleteWebhook(webhook: WebhookEntry) {
    this.webhookService.deleteWebhook(webhook.data.id).subscribe((res) => {
      if (res) {
        const index = this.activeWebhooks.findIndex((x) => x === webhook);

        if (index > -1) {
          this.activeWebhooks.splice(index, 1);
        }
      }
    });
  }

  testWebhook(webhook: WebhookEntry) {
    webhook.testing = true;

    this.webhookService.testWebhook(webhook.data.id).subscribe((res) => {
      webhook.testResult = res.success;
      webhook.testing = false;

      if (!webhook.testResult) {
        webhook.testResult = false;
        this.toast.open(
          `${res.message ? res.message : 'An error occurred testing the webhook'}`,
          'Dismiss',
          NotificationType.Error
        );
      }

      setTimeout(() => {
        webhook.testResult = null;
      }, 2000);
    });
  }

  updateWebhook(webhook: WebhookEntry) {
    if (!this.validUrl(webhook.data.url)) {
      this.toast.open(`Webhooks should start with 'http://' or 'https://'`, 'Dismiss', NotificationType.Error);
      return;
    }

    if (webhook.type === WebhookType.Next) {
      const pendingTypes = webhook.pendingTypes || webhook.data.types || [];
      if (pendingTypes.length === 0) {
        this.toast.open('At least one webhook type must be selected', 'Dismiss', NotificationType.Error);
        return;
      }
    }

    this.setSavedStatus(webhook, 1);

    const updates = {
      url: webhook.data.url,
      ...(webhook.pendingTypes !== undefined && { types: webhook.pendingTypes }),
    };

    this.webhookService.updateWebhook(webhook.data.id, updates).subscribe((response: WebhookResponse) => {
      webhook.editingUrl = false;
      if (webhook.pendingTypes !== undefined) {
        webhook.data.types = webhook.pendingTypes;
        webhook.pendingTypes = undefined;
      }
      this.handleSavedStatus(webhook, response);
    });
  }

  updateWebhookName(webhook: WebhookEntry) {
    this.setSavedStatus(webhook, 1);

    this.webhookService.updateWebhookName(webhook.data).subscribe((response: WebhookResponse) => {
      this.handleSavedStatus(webhook, response);
    });
  }

  setSavedStatus(webhook: WebhookEntry, status: number) {
    webhook.saveStatus = status;
    this.saveStatus = status;
  }

  handleSavedStatus(webhook: WebhookEntry, response: WebhookResponse) {
    if (response.error) {
      this.setSavedStatus(webhook, -1);
      this.toast.open(response.error, 'Dismiss', NotificationType.Error);
    } else {
      this.setSavedStatus(webhook, 2);
      setTimeout(() => {
        this.setSavedStatus(webhook, 0);
      }, 2000);
    }
  }

  handleTypeChange(webhook: WebhookEntry, type: WebhookEventType, checked: boolean) {
    webhook.pendingTypes = webhook.pendingTypes || [...(webhook.data.types || [])];
    if (checked) {
      webhook.pendingTypes.push(type);
    } else {
      webhook.pendingTypes = webhook.pendingTypes.filter((t) => t !== type);
    }
  }

  updateWebhookTypes(webhook: WebhookEntry, triggers: WebhookEventType[]) {
    this.setSavedStatus(webhook, 1);
    webhook.data.types = triggers;

    this.webhookService.updateWebhookTypes(webhook.data).subscribe((response: WebhookResponse) => {
      this.handleSavedStatus(webhook, response);
    });
  }

  cancelEditing(webhook: WebhookEntry) {
    webhook.editingUrl = false;
    webhook.pendingTypes = undefined;
  }

  sendHistoricDataPrompt(webhook: WebhookEntry) {
    const dialogRef = this.dialog.open(DialogConfirmSendLegacyWebhooksComponent, {
      width: '500px',
      maxWidth: '90vw',
      panelClass: 'dialog-default',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        webhook.sendStatus = 1;
        this.webhookService.sendAllWebhooks(webhook.data.id).subscribe(
          (res) => {
            webhook.sendStatus = res ? 2 : -1;
            setTimeout(() => {
              webhook.sendStatus = null;
            }, 2000);
          },
          () => {
            webhook.sendStatus = -1;
            setTimeout(() => {
              webhook.sendStatus = null;
            }, 2000);
          }
        );
      }
    });
  }
}
